import { request } from '@/utils/ajax';
import { PaginatedSearchRequest, PaginationResult } from '@/types';

export type MessageStatus = 'Sent' | 'Pending' | 'Failed';
/** Empty string = no status filter */
export type MessageStatusFilter = MessageStatus | '';

export interface MessageSendAttempt {
    attemptNumber: number;
    result: 'Success' | 'Failure';
    time: string;
    error: string | null;
}

export interface MessageLogFile {
    fileName: string;
    contentType: string;
    size: number;
}

export interface MessageLogAttachment {
    fileId: string;
    fileName: string;
    contentType: string;
    size: number;
}

export interface OutgoingEmailLogItem {
    id: string;
    createdAt: string;
    to: string[];
    subject: string;
    status: MessageStatus;
    sendTime: string | null;
    attemptCount: number;
    maxAttempts: number;
    attachmentCount: number;
    lastError: string | null;
}

export interface OutgoingEmailLogDetail extends OutgoingEmailLogItem {
    awsEmailId: string | null;
    cc: string[];
    bcc: string[];
    body: MessageLogFile | null;
    attachments: MessageLogAttachment[];
    sendAttempts: MessageSendAttempt[];
}

export interface OutgoingSmsLogItem {
    id: string;
    createdAt: string;
    to: string;
    body: string;
    status: MessageStatus;
    sendTime: string | null;
    attemptCount: number;
    maxAttempts: number;
    lastError: string | null;
}

export interface OutgoingSmsLogDetail extends OutgoingSmsLogItem {
    sendAttempts: MessageSendAttempt[];
}

export interface ReceivedEmailLogItem {
    id: string;
    receivedAt: string;
    from: string[];
    to: string[];
    subject: string | null;
    projectId: number;
    projectName: string | null;
    type: 'Case' | 'Other';
    status: 'New' | 'Read' | 'Archived';
    attachmentCount: number;
    hasHtml: boolean;
    hasText: boolean;
}

export interface ReceivedEmailLogDetail extends ReceivedEmailLogItem {
    awsEmailId: string | null;
    returnPath: string | null;
    messageId: string | null;
    source: string | null;
    destination: string[];
    attachments: MessageLogAttachment[];
}

export function getOutgoingEmails(
    req: PaginatedSearchRequest,
    status: MessageStatusFilter
): Promise<PaginationResult<OutgoingEmailLogItem>> {
    return request('api/communicore/outgoing-emails', { query: { ...req, status } }).then((x) => x.json());
}

export function getOutgoingEmail(id: string): Promise<OutgoingEmailLogDetail> {
    return request(`api/communicore/outgoing-emails/${id}`).then((x) => x.json());
}

export function getOutgoingEmailBodyUrl(id: string) {
    return `api/communicore/outgoing-emails/${id}/body`;
}

export function getOutgoingEmailAttachmentUrl(id: string, fileId: string) {
    return `api/communicore/outgoing-emails/${id}/attachments/${fileId}`;
}

export function getOutgoingSmses(
    req: PaginatedSearchRequest,
    status: MessageStatusFilter
): Promise<PaginationResult<OutgoingSmsLogItem>> {
    return request('api/communicore/outgoing-smses', { query: { ...req, status } }).then((x) => x.json());
}

export function getOutgoingSms(id: string): Promise<OutgoingSmsLogDetail> {
    return request(`api/communicore/outgoing-smses/${id}`).then((x) => x.json());
}

export function getReceivedEmails(req: PaginatedSearchRequest): Promise<PaginationResult<ReceivedEmailLogItem>> {
    return request('api/communicore/received-emails', { query: req }).then((x) => x.json());
}

export function getReceivedEmail(id: string): Promise<ReceivedEmailLogDetail> {
    return request(`api/communicore/received-emails/${id}`).then((x) => x.json());
}

export function getReceivedEmailContentUrl(id: string, contentType: 'html' | 'text') {
    return `api/communicore/received-emails/${id}/content/${contentType}`;
}

export function getReceivedEmailAttachmentUrl(id: string, fileId: string) {
    return `api/communicore/received-emails/${id}/attachments/${fileId}`;
}
